import { createRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Paperclip } from "lucide-react";
import type { ParseJobDto, ParseJobStatusDto } from "@carry/contract";
import { Route as RootRoute } from "./__root";
import { useAuth } from "../providers/auth-context";
import { useAdminApi } from "../providers/api-context";
import { TableSkeletonRows } from "../components/table-skeleton";
import { formatDateTime as formatDisplayDateTime } from "../lib/format";

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8080";

type Status = ParseJobStatusDto;
type StatusFilter = Status | "all";

const STATUS_FILTERS: ReadonlyArray<StatusFilter> = [
  "all",
  "pending",
  "processing",
  "completed",
  "failed",
];

function isStatusFilter(v: unknown): v is StatusFilter {
  return typeof v === "string" && (STATUS_FILTERS as readonly string[]).includes(v);
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/parse-jobs",
  validateSearch: (search) => ({
    filter: isStatusFilter(search.filter) ? search.filter : ("all" as StatusFilter),
  }),
  beforeLoad: ({ context }) => {
    if (context.authLoading) return;
    if (!context.session) throw redirect({ to: "/auth/login" });
  },
  component: ParseJobsPage,
});

const STATUS_META: Record<Status, { label: string; bg: string; ink: string }> = {
  pending: { label: "待機", bg: "var(--st-draft-bg)", ink: "var(--st-draft-ink)" },
  processing: { label: "処理中", bg: "var(--st-transit-bg)", ink: "var(--st-transit-ink)" },
  completed: { label: "完了", bg: "var(--st-done-bg)", ink: "var(--st-done-ink)" },
  failed: { label: "失敗", bg: "var(--st-cancel-bg)", ink: "var(--st-cancel-ink)" },
};

function ParseJobsPage() {
  const { session } = useAuth();
  const api = useAdminApi();
  const { filter } = Route.useSearch();
  const navigate = useNavigate({ from: "/parse-jobs" });
  const setFilter = (next: StatusFilter) => navigate({ search: { filter: next } });

  const { data, isLoading, error } = useQuery({
    queryKey: ["parse-jobs", session?.user?.id, filter],
    queryFn: () => api.parseJob.list({ limit: 100, status: filter === "all" ? undefined : filter }),
    enabled: !!session,
    refetchInterval: filter === "processing" || filter === "pending" ? 5000 : 15000,
  });

  const rows: ParseJobDto[] = data?.data ?? [];
  const counts = rows.reduce(
    (acc, r) => {
      acc.all++;
      acc[r.status]++;
      return acc;
    },
    { all: 0, pending: 0, processing: 0, completed: 0, failed: 0 } as Record<
      Status | "all",
      number
    >,
  );

  // KPI: 平均 confidence / 平均処理時間 / 失敗率
  const completed = rows.filter((r) => r.status === "completed");
  const avgConfidence =
    completed.length === 0
      ? null
      : Math.round(completed.reduce((s, r) => s + (r.confidence ?? 0), 0) / completed.length);
  const withDuration = rows.filter((r) => r.started_at && r.completed_at);
  const avgDurationMs =
    withDuration.length === 0
      ? null
      : Math.round(
          withDuration.reduce(
            (s, r) => s + (new Date(r.completed_at!).getTime() - new Date(r.started_at!).getTime()),
            0,
          ) / withDuration.length,
        );
  const failureRate = counts.all === 0 ? 0 : Math.round((counts.failed / counts.all) * 100);

  return (
    <div style={{ margin: -24 }}>
      <div
        className="cp-pageheader"
        style={{
          padding: "12px 32px",
          borderBottom: "0.5px solid var(--rule-softer)",
          background: "var(--bg)",
        }}
      ></div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          background: "var(--bg-raised)",
          borderBottom: "0.5px solid var(--rule)",
        }}
      >
        <Kpi label="登録ジョブ" value={String(counts.all)} unit="件" />
        <Kpi
          label="平均 confidence"
          value={avgConfidence == null ? "" : String(avgConfidence)}
          unit={avgConfidence == null ? "" : "%"}
          ink="var(--accent)"
          right
        />
        <Kpi
          label="平均処理時間"
          value={avgDurationMs == null ? "" : (avgDurationMs / 1000).toFixed(2)}
          unit={avgDurationMs == null ? "" : "秒"}
          ink="var(--info)"
          right
        />
        <Kpi
          label="失敗率"
          value={String(failureRate)}
          unit="%"
          ink={failureRate > 10 ? "var(--danger)" : failureRate > 5 ? "var(--warn)" : "var(--ok)"}
          right
          emphasise={failureRate > 10}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: "0.5px solid var(--rule)",
          padding: "0 32px",
          background: "var(--bg-panel)",
        }}
      >
        {STATUS_FILTERS.map((t) => {
          const active = filter === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setFilter(t)}
              style={{
                padding: "10px 16px",
                fontSize: 12,
                color: active ? "var(--ink)" : "var(--ink-3)",
                fontWeight: active ? 500 : 400,
                borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {t === "all" ? "すべて" : STATUS_META[t].label}
              <span
                style={{
                  marginLeft: 6,
                  fontFamily: "var(--f-mono)",
                  fontSize: 10,
                  color: "var(--ink-4)",
                }}
              >
                {counts[t]}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ padding: "16px 32px 32px" }}>
        {error && (
          <div
            role="alert"
            style={{
              padding: "10px 14px",
              background: "oklch(0.96 0.05 30)",
              border: "0.5px solid var(--danger)",
              color: "var(--danger)",
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            取得に失敗しました: {String(error)}
          </div>
        )}
        <table className="tbl" style={{ fontSize: 12, marginTop: 4 }}>
          <thead>
            <tr>
              <th style={{ width: 170 }}>作成日時</th>
              <th style={{ width: 100 }}>Parser</th>
              <th className="num" style={{ width: 90 }}>
                Confidence
              </th>
              <th className="num" style={{ width: 90 }}>
                処理時間
              </th>
              <th className="num" style={{ width: 60 }}>
                Retry
              </th>
              <th>エラー</th>
              <th style={{ width: 80 }}>状態</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <TableSkeletonRows rows={6} columns={8} />}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}
                >
                  該当する解析ジョブはありません
                </td>
              </tr>
            )}
            {rows.map((j) => (
              <tr key={j.public_id}>
                <td className="num" style={{ fontSize: 11, color: "var(--ink-2)" }}>
                  {formatParseJobDateTime(j.created_at)}
                </td>
                <td className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
                  {j.parser_id ?? ""}
                </td>
                <td className="num" style={{ fontSize: 11 }}>
                  {j.confidence == null ? (
                    <span style={{ color: "var(--ink-4)" }}></span>
                  ) : (
                    <span style={{ color: j.confidence >= 80 ? "var(--ok)" : "var(--warn)" }}>
                      {j.confidence}%
                    </span>
                  )}
                </td>
                <td className="num" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                  {formatDuration(j.started_at, j.completed_at)}
                </td>
                <td
                  className="num"
                  style={{
                    fontSize: 11,
                    color: j.retry_count > 0 ? "var(--warn)" : "var(--ink-4)",
                  }}
                >
                  {j.retry_count}
                </td>
                <td
                  style={{
                    fontSize: 10,
                    color: "var(--danger)",
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={j.error_message ?? undefined}
                >
                  {j.error_message ?? <span style={{ color: "var(--ink-4)" }}></span>}
                </td>
                <td>
                  <StatusChip status={j.status} />
                </td>
                <td>
                  <a
                    href={`${API_BASE_URL}/api/v1/uploaded-files/${j.uploaded_file_public_id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: 11,
                      color: "var(--accent)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Paperclip size={11} /> 原本
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: Status }) {
  const m = STATUS_META[status];
  return (
    <span className="chip" style={{ background: m.bg, color: m.ink, border: 0 }}>
      <span className="dot" />
      {m.label}
    </span>
  );
}

function Kpi({
  label,
  value,
  unit,
  ink,
  right,
  emphasise,
}: {
  label: string;
  value: string;
  unit: string;
  ink?: string;
  right?: boolean;
  emphasise?: boolean;
}) {
  return (
    <div
      style={{
        padding: "18px 24px",
        borderRight: right ? "0.5px solid var(--rule-soft)" : "none",
        background: emphasise ? "var(--st-cancel-bg, oklch(0.96 0.04 30))" : "transparent",
      }}
    >
      <div className="eyebrow">{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 4 }}>
        <div
          className="num"
          style={{
            fontSize: 28,
            fontWeight: emphasise ? 700 : 500,
            color: ink ?? "var(--ink)",
          }}
        >
          {value}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{unit}</div>
        {emphasise && (
          <span
            style={{
              marginLeft: 4,
              fontSize: 10,
              fontWeight: 600,
              color: "var(--danger)",
              padding: "1px 6px",
              border: "0.5px solid var(--danger)",
              borderRadius: 2,
            }}
          >
            要対応
          </span>
        )}
      </div>
    </div>
  );
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return "";
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatParseJobDateTime(iso: string): string {
  const formatted = formatDisplayDateTime(iso);
  return formatted === "—" ? "" : formatted;
}
